import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinTable,
	ManyToMany,
	OneToMany,
	OneToOne,
} from 'typeorm';
import {
	booleanTransformer,
	GenPartial,
	momentTransformer,
	timeTransformer,
} from 'typeormgen';
import Actor from '../actors/actor.entity';
import CmEpisodicActor from '../cm-episodic-actors/cm-episodic-actor.entity';
import EpisodicActorPicture from '../episodic-actor-pictures/episodic-actor-picture.entity';
import EpisodicBase from '../episodic-base.entity';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import HaEpisodicActor from '../ha-episodic-actors/ha-episodic-actor.entity';
import { Indexable } from '../indexable';
import MuEpisodicActor from '../mu-episodic-actors/mu-episodic-actor.entity';
import Picture from '../pictures/picture.entity';
import PrEpisodicActor from '../pr-episodic-actors/pr-episodic-actor.entity';
import { Taggable } from '../taggable';
import { EpisodicActorESDoc } from './episodic-actor.interfaces';

@Entity('episodic_actors')
export default class EpisodicActor
	extends EpisodicBase
	implements Indexable<EpisodicActorESDoc>, Taggable {
	static readonly tableName = 'episodic_actors';

	static columns = [
		'id',
		'episodic_id',
		'first_name',
		'last_name',
		'gender',
		'age',
		'height',
		'weight',
		'fitting_date',
		'fitting_time',
		'fitting_note',
		'notes',
		'allergies',
		'blouse',
		'bra',
		'chest',
		'dress',
		'eyes',
		'hair',
		'hat',
		'hip',
		'inseam',
		'pant',
		'piercings',
		'shirt',
		'shoe',
		'skirt',
		'suit',
		'tattoos',
		'tshirt',
		'waist',
		'glove',
		'skin_tone',
		'ring_size',
		'ears_pierced',
		'scars',
		'start_work_date',
		'is_start_work_date_manual',
	];

	static readonly sortColumns = ['name', 'char_ids', 'next_fitting'];

	@Column('int')
	series_actor_id: number;

	@Column('varchar')
	first_name: string;

	@Column('varchar')
	last_name: string;

	@Column('int')
	gender: number;

	@Column('varchar')
	age: string;

	@Column('varchar')
	email_primary: string;

	@Column('varchar')
	email_secondary: string;

	@Column('varchar')
	phone_primary: string;

	@Column('varchar')
	phone_secondary: string;

	@Column('varchar')
	height: string;

	@Column('varchar')
	weight: string;

	@Column({ type: 'date', transformer: momentTransformer })
	fitting_date: Moment;

	/** Unlike every other time, this one is stored as Local not UTC. What the user picks is what goes in the DB. */
	@Column({ type: 'time', transformer: timeTransformer })
	fitting_time: Moment;

	@Column('varchar')
	fitting_note: string;

	@Column('varchar')
	notes: string;

	@Column('varchar')
	allergies: string;

	@Column('varchar')
	blouse: string;

	@Column('varchar')
	bra: string;

	@Column('varchar')
	chest: string;

	@Column('varchar')
	dress: string;

	@Column('varchar')
	eyes: string;

	@Column('varchar')
	hair: string;

	@Column('varchar')
	hat: string;

	@Column('varchar')
	hip: string;

	@Column('varchar')
	inseam: string;

	@Column('varchar')
	pant: string;

	@Column('varchar')
	piercings: string;

	@Column('varchar')
	shirt: string;

	@Column('varchar')
	shoe: string;

	@Column('varchar')
	skirt: string;

	@Column('varchar')
	suit: string;

	@Column('varchar')
	tattoos: string;

	@Column('varchar')
	waist: string;

	@Column('varchar')
	glove: string;

	@Column('varchar')
	skin_tone: string;

	@Column('varchar')
	ring_size: string;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	ears_pierced: boolean;

	@Column('varchar')
	scars: string;

	@Column('varchar')
	tshirt: string;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column({ type: 'date', transformer: momentTransformer })
	start_work_date: Moment;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	is_start_work_date_manual: boolean;

	@OneToOne(
		(type) => EpisodicActorPicture,
		(episodicActorPicture) => episodicActorPicture.episodicActor
	)
	picture: EpisodicActorPicture;

	@ManyToMany(() => Picture, (pic) => pic.episodicActorPicture)
	@JoinTable({
		name: 'episodic_actor_pictures',
		joinColumn: { name: 'episodic_actor_id' },
		inverseJoinColumn: { name: 'picture_id' },
	})
	/** Really should only ever have one picture in here */
	pics: Picture[];

	@OneToMany(
		(type) => EpisodicCharacter,
		(episodicCharacter) => episodicCharacter.episodicActor
	)
	episodicCharacters: EpisodicCharacter[];

	main_photo_id: number;

	@OneToOne((type) => Actor, (actor) => actor.episodicActor)
	actor: Actor;

	@OneToOne(
		(type) => CmEpisodicActor,
		(cmEpisodicActor) => cmEpisodicActor.episodicActor
	)
	cmEpisodicActor: CmEpisodicActor;

	@OneToOne(
		(type) => HaEpisodicActor,
		(haEpisodicActor) => haEpisodicActor.episodicActor
	)
	haEpisodicActor: HaEpisodicActor;

	@OneToOne(
		(type) => MuEpisodicActor,
		(muEpisodicActor) => muEpisodicActor.episodicActor
	)
	muEpisodicActor: MuEpisodicActor;

	@OneToOne(
		(type) => PrEpisodicActor,
		(prEpisodicActor) => prEpisodicActor.episodicActor
	)
	prEpisodicActor: PrEpisodicActor;

	static rules() {
		return {
			...super.rules(),
			series_actor_id: Joi.number().integer().allow(null),
			age: Joi.string().max(100).allow(null, ''),
			allergies: Joi.string().max(1000).allow(null, ''),
			blouse: Joi.string().max(300).allow(null, ''),
			bra: Joi.string().max(300).allow(null, ''),
			chest: Joi.string().max(100).allow(null, ''),
			date_created: Joi.date().allow(null),
			dress: Joi.string().max(300).allow(null, ''),
			ears_pierced: Joi.boolean().allow(null),
			email_primary: Joi.string().max(255).allow(null, ''),
			email_secondary: Joi.string().max(255).allow(null, ''),
			eyes: Joi.string().max(300).allow(null, ''),
			first_name: Joi.string().max(100),
			fitting_note: Joi.string().max(500).allow(null, ''),
			fitting_date: Joi.date().allow(null),
			// this being a Joi.date converted the value to a Date, which breaks timeTransformer.from in typeormgen v0.11.3
			fitting_time: Joi.string().allow(null),
			gender: Joi.number().integer().allow(null),
			glove: Joi.string().max(300).allow(null, ''),
			hair: Joi.string().max(300).allow(null, ''),
			hat: Joi.string().max(300).allow(null, ''),
			height: Joi.string().max(100).allow(null, ''),
			hip: Joi.string().max(300).allow(null, ''),
			inseam: Joi.string().max(300).allow(null, ''),
			last_name: Joi.string().max(100).allow(null, ''),
			last_updated_time: Joi.date().allow(null),
			main_photo_id: Joi.number().integer().allow(null),
			notes: Joi.string().max(1000).allow(null, ''),
			pant: Joi.string().max(300).allow(null, ''),
			phone_primary: Joi.string().max(20).allow(null, ''),
			phone_secondary: Joi.string().max(20).allow(null, ''),
			piercings: Joi.string().max(1000).allow(null, ''),
			ring_size: Joi.string().max(300).allow(null, ''),
			scars: Joi.string().max(300).allow(null, ''),
			shirt: Joi.string().max(300).allow(null, ''),
			shoe: Joi.string().max(300).allow(null, ''),
			skin_tone: Joi.string().max(300).allow(null, ''),
			skirt: Joi.string().max(300).allow(null, ''),
			start_work_date: Joi.date().allow(null),
			is_start_work_date_manual: Joi.boolean().allow(null),
			suit: Joi.string().max(300).allow(null, ''),
			tattoos: Joi.string().max(1000).allow(null, ''),
			tshirt: Joi.string().max(300).allow(null, ''),
			waist: Joi.string().max(300).allow(null, ''),
			weight: Joi.string().max(300).allow(null, ''),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			series_actor_id: this.series_actor_id,
			age: this.age,
			allergies: this.allergies,
			blouse: this.blouse,
			bra: this.bra,
			chest: this.chest,
			date_created: this.date_created,
			dress: this.dress,
			ears_pierced: this.ears_pierced,
			email_primary: this.email_primary,
			email_secondary: this.email_secondary,
			eyes: this.eyes,
			first_name: this.first_name,
			fitting_note: this.fitting_note,
			fitting_date: this.fitting_date,
			fitting_time: timeTransformer.to(this.fitting_time),
			gender: this.gender,
			glove: this.glove,
			hair: this.hair,
			hat: this.hat,
			height: this.height,
			hip: this.hip,
			inseam: this.inseam,
			last_name: this.last_name,
			last_updated_time: this.last_updated_time,
			main_photo_id: this.main_photo_id,
			notes: this.notes,
			pant: this.pant,
			phone_primary: this.phone_primary,
			phone_secondary: this.phone_secondary,
			piercings: this.piercings,
			ring_size: this.ring_size,
			scars: this.scars,
			shirt: this.shirt,
			shoe: this.shoe,
			skin_tone: this.skin_tone,
			skirt: this.skirt,
			start_work_date: this.start_work_date || null,
			is_start_work_date_manual: this.is_start_work_date_manual || null,
			suit: this.suit,
			tattoos: this.tattoos,
			tshirt: this.tshirt,
			waist: this.waist,
			weight: this.weight,
		};
	}

	toESDoc() {
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			first_name: this.first_name,
			last_name: this.last_name,
		};
	}

	assign(props?: GenPartial<EpisodicActor>) {
		super.assign(props);

		if (props) {
			if (typeof props.fitting_date !== 'undefined') {
				this.fitting_date = momentTransformer.from(props.fitting_date);
			}
			if (typeof props.fitting_time !== 'undefined') {
				this.fitting_time = timeTransformer.from(props.fitting_time);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
			if (typeof props.start_work_date !== 'undefined') {
				this.start_work_date = momentTransformer.from(props.start_work_date);
			}
			if (typeof props.is_start_work_date_manual !== 'undefined') {
				this.is_start_work_date_manual = booleanTransformer.from(
					props.is_start_work_date_manual
				);
			}
			if (typeof props.ears_pierced !== 'undefined') {
				this.ears_pierced = booleanTransformer.from(props.ears_pierced);
			}
		}
	}

	constructor(props?: GenPartial<EpisodicActor>) {
		super(props);
	}

	getFullName() {
		let fullName = '';
		if (typeof this.first_name !== 'undefined' && this.first_name !== null) {
			fullName += this.first_name;
		}
		if (typeof this.last_name !== 'undefined' && this.last_name !== null) {
			fullName += ` ${this.last_name}`;
		}
		return fullName.trim();
	}

	getTagName() {
		return this.getFullName();
	}
}
